import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { FormEvent, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
    Button,
    Field,
    FormActions,
    FormSection,
    IconButton,
    InlineBanner,
    Input,
    notify,
    PageHeader,
    RadioGroup,
    Select,
    Skeleton,
    SkeletonRegion,
    StateBlock,
    Switch,
} from '../../components/ui';
import { ROLES, RoleName } from '../../constants/roles';
import { useRemote } from '../../features/home/useRemote';
import { useApiError } from '../../hooks/useApiError';
import { usePageTitle } from '../../hooks/usePageTitle';
import api, { asPlanLimit, getApiError } from '../../services/api';
import type { RootState } from '../../store';
import type { Location } from '../../types/Location';
import type { Restaurant } from '../../types/Restaurant';
import type { User } from '../../types/User';

const MIN_PASSWORD = 8;
const ALL_LOCATIONS = '__all';

interface Values {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    role: RoleName;
    restaurantId: string | null;
    locationId: string | null;
    isActive: boolean;
}

type Key = keyof Values;
type Errors = Partial<Record<Key, string>>;

interface RoleRow {
    id: string;
    name: string;
}

const EMPTY: Values = { firstName: '', lastName: '', email: '', password: '', role: 'Staff', restaurantId: null, locationId: null, isActive: true };

const fromUser = (u: User): Values => ({
    firstName: u.firstName ?? '',
    lastName: u.lastName ?? '',
    email: u.email,
    password: '',
    role: u.role.name,
    restaurantId: u.restaurant?.id ?? null,
    locationId: u.location?.id ?? null,
    isActive: u.isActive ?? true,
});

const FormBody = ({ id, initial }: { id: string | undefined; initial: Values }) => {
    const { t } = useTranslation('team');
    const navigate = useNavigate();
    const apiError = useApiError();
    const me = useSelector((s: RootState) => s.auth.user);
    const isAdmin = me?.role?.name === 'Admin';
    const editingSelf = id !== undefined && id === me?.id;

    const [values, setValues] = useState<Values>(initial);
    const [errors, setErrors] = useState<Errors>({});
    const [banner, setBanner] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [shown, setShown] = useState(false);
    const edited = useRef(new Set<Key>());
    const form = useRef<HTMLFormElement>(null);

    const roles = useRemote('roles', () => api.get<RoleRow[]>('/roles').then((r) => r.data));
    const restaurants = useRemote(isAdmin ? 'restaurants' : null, () => api.get<Restaurant[]>('/restaurants').then((r) => r.data));
    const restaurantId = isAdmin ? values.restaurantId : (me?.restaurant?.id ?? null);
    const needsLocation = values.role === 'Staff' || values.role === 'Manager';
    const locations = useRemote(restaurantId && needsLocation ? `locations:${restaurantId}` : null, () =>
        api.get<Location[]>(`/restaurants/${restaurantId}/locations`).then((r) => r.data)
    );

    const offered: readonly RoleName[] = isAdmin ? ROLES : ROLES.filter((r) => r === 'Manager' || r === 'Staff');
    // The role being edited stays visible even when the caller could not assign it (an Owner editing themselves).
    const roleOptions = (offered.includes(values.role) ? offered : [values.role, ...offered]).map((r) => ({
        value: r,
        label: `${t(`roles.${r}`)}: ${t(`roleHelp.${r}`)}`,
        disabled: editingSelf,
    }));

    const dirty = useMemo(() => JSON.stringify(values) !== JSON.stringify(initial), [values, initial]);

    const validate = (v: Values): Errors => {
        const e: Errors = {};
        if (!v.firstName.trim()) e.firstName = t('form.errors.required');
        if (!v.lastName.trim()) e.lastName = t('form.errors.required');
        if (!v.email.trim()) e.email = t('form.errors.required');
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email.trim())) e.email = t('form.errors.email');
        if (!id && v.password.length < MIN_PASSWORD) e.password = t('form.errors.password');
        if (v.role !== 'Admin' && isAdmin && !v.restaurantId) e.restaurantId = t('form.errors.restaurant');
        if (v.role === 'Staff' && !v.locationId) e.locationId = t('form.errors.location');
        return e;
    };

    const set = <K extends Key>(key: K, value: Values[K]) => {
        edited.current.add(key);
        setValues((v) => ({
            ...v,
            [key]: value,
            ...(key === 'restaurantId' ? { locationId: null } : {}),
            ...(key === 'role' && (value === 'Owner' || value === 'Admin') ? { locationId: null } : {}),
        }));
        if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
    };

    const blur = (key: Key) => {
        if (!edited.current.has(key)) return;
        setErrors((e) => ({ ...e, [key]: validate(values)[key] }));
    };

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        const found = validate(values);
        setErrors(found);
        const first = (['firstName', 'lastName', 'email', 'password', 'restaurantId', 'locationId'] as const).find((k) => found[k]);
        if (first) {
            setBanner(t('form.summary'));
            const target = form.current?.querySelector<HTMLElement>(`[data-field="${first}"]`);
            (target?.matches('input') ? target : target?.querySelector('button'))?.focus();
            return;
        }
        const roleId = roles.data?.find((r) => r.name === values.role)?.id;
        if (!roleId) {
            setBanner(apiError(null));
            return;
        }
        setBanner(null);
        setSubmitting(true);
        const keepsLocation = values.role === 'Staff' || values.role === 'Manager';
        const body = {
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
            email: values.email.trim(),
            roleId,
            ...(isAdmin && values.role !== 'Admin' ? { restaurantId: values.restaurantId } : {}),
            ...(id ? { locationId: keepsLocation ? values.locationId : null } : keepsLocation && values.locationId ? { locationId: values.locationId } : {}),
            ...(id ? {} : { password: values.password }),
            ...(id && !editingSelf ? { isActive: values.isActive } : {}),
        };
        try {
            if (id) await api.put(`/users/${id}`, body);
            else await api.post('/users', body);
            notify.success(t(id ? 'form.saved' : 'form.created'));
            navigate('/users');
        } catch (err) {
            const limit = asPlanLimit(err);
            if (!limit && getApiError(err).status === 409) {
                // The only conflict on a user is an email that is already taken.
                setErrors({ email: t('form.errors.emailTaken') });
                setBanner(t('form.summary'));
                form.current?.querySelector<HTMLElement>('[data-field="email"]')?.focus();
            } else {
                setBanner(limit ? `${apiError(err)} ${t('form.upgrade')}` : apiError(err));
            }
            setSubmitting(false);
        }
    };

    const locationOptions = [
        ...(values.role === 'Manager' ? [{ value: ALL_LOCATIONS, label: t('list.allLocations') }] : []),
        ...(locations.data ?? []).map((l) => ({ value: l.id, label: l.name })),
    ];

    return (
        <form ref={form} onSubmit={(e) => void submit(e)} noValidate className="max-w-form">
            <div className="grid gap-4">
                {banner && <InlineBanner tone="danger">{banner}</InlineBanner>}

                <FormSection title={t('form.person')}>
                    <div className="grid gap-4 sm:grid-cols-2 sm:gap-5">
                        <Field label={t('form.firstName')} error={errors.firstName}>
                            {(c) => (
                                <Input
                                    {...c}
                                    data-field="firstName"
                                    value={values.firstName}
                                    onChange={(e) => set('firstName', e.target.value)}
                                    onBlur={() => blur('firstName')}
                                    autoComplete="off"
                                    required
                                />
                            )}
                        </Field>
                        <Field label={t('form.lastName')} error={errors.lastName}>
                            {(c) => (
                                <Input
                                    {...c}
                                    data-field="lastName"
                                    value={values.lastName}
                                    onChange={(e) => set('lastName', e.target.value)}
                                    onBlur={() => blur('lastName')}
                                    autoComplete="off"
                                    required
                                />
                            )}
                        </Field>
                    </div>
                    <Field label={t('form.email')} error={errors.email}>
                        {(c) => (
                            <Input
                                {...c}
                                data-field="email"
                                type="email"
                                inputMode="email"
                                value={values.email}
                                onChange={(e) => set('email', e.target.value)}
                                onBlur={() => blur('email')}
                                autoComplete="off"
                                spellCheck={false}
                                required
                            />
                        )}
                    </Field>
                </FormSection>

                <FormSection title={t('form.access')}>
                    <fieldset className="grid gap-1">
                        <legend className="mb-1.5 text-small font-medium text-strong">{t('form.role')}</legend>
                        <RadioGroup
                            aria-label={t('form.role')}
                            value={values.role}
                            onChange={(r) => set('role', r as RoleName)}
                            options={roleOptions}
                        />
                    </fieldset>
                    {isAdmin && values.role !== 'Admin' && (
                        <div data-field="restaurantId">
                            <Field label={t('form.restaurant')} error={errors.restaurantId}>
                                {(c) => (
                                    <Select
                                        {...c}
                                        value={values.restaurantId}
                                        onChange={(v) => set('restaurantId', v)}
                                        options={(restaurants.data ?? []).map((r) => ({ value: r.id, label: r.name }))}
                                        placeholder={t('form.restaurantPlaceholder')}
                                        invalid={Boolean(errors.restaurantId)}
                                    />
                                )}
                            </Field>
                        </div>
                    )}
                    {id && !editingSelf && (
                        <Switch checked={values.isActive} onChange={(on) => set('isActive', on)} label={t('form.active')} description={t('form.activeHint')} />
                    )}
                    {needsLocation && (
                        <div data-field="locationId">
                            <Field
                                label={t('form.location')}
                                error={errors.locationId}
                                hint={values.role === 'Manager' ? t('form.locationOptional') : undefined}
                            >
                                {(c) => (
                                    <Select
                                        {...c}
                                        value={values.locationId ?? (values.role === 'Manager' ? ALL_LOCATIONS : null)}
                                        onChange={(v) => set('locationId', v === ALL_LOCATIONS ? null : v)}
                                        options={locationOptions}
                                        placeholder={t('form.locationPlaceholder')}
                                        invalid={Boolean(errors.locationId)}
                                        disabled={!restaurantId}
                                    />
                                )}
                            </Field>
                        </div>
                    )}
                </FormSection>

                {!id && (
                    <FormSection title={t('form.passwordTitle')}>
                        <Field label={t('form.password')} hint={t('form.passwordHint')} error={errors.password}>
                            {(c) => (
                                <Input
                                    {...c}
                                    data-field="password"
                                    type={shown ? 'text' : 'password'}
                                    value={values.password}
                                    onChange={(e) => set('password', e.target.value)}
                                    onBlur={() => blur('password')}
                                    autoComplete="new-password"
                                    minLength={MIN_PASSWORD}
                                    required
                                    trailing={
                                        <IconButton
                                            aria-label={shown ? t('form.hide') : t('form.show')}
                                            aria-pressed={shown}
                                            size="sm"
                                            onClick={() => setShown((v) => !v)}
                                        >
                                            {shown ? <EyeSlashIcon className="size-5" aria-hidden="true" /> : <EyeIcon className="size-5" aria-hidden="true" />}
                                        </IconButton>
                                    }
                                />
                            )}
                        </Field>
                    </FormSection>
                )}
            </div>
            <FormActions
                onCancel={() => navigate('/users')}
                dirty={dirty}
                submitting={submitting}
                submitLabel={id ? undefined : t('form.create')}
            />
        </form>
    );
};

/** Create at /users/create, edit at /users/edit/:id. The detail view is the edit form. */
const UserForm = () => {
    const { t } = useTranslation('team');
    const { id } = useParams<{ id: string }>();
    usePageTitle(t(id ? 'form.editTitle' : 'form.createTitle'));
    const user = useRemote(id ? `user:${id}` : null, () => api.get<User>(`/users/${id}`).then((r) => r.data));

    const header = <PageHeader title={t(id ? 'form.editTitle' : 'form.createTitle')} back={{ to: '/users', label: t('form.back') }} />;

    if (id && user.state === 'loading') {
        return (
            <>
                {header}
                <SkeletonRegion className="grid max-w-form gap-4">
                    <Skeleton className="h-48 w-full" />
                    <Skeleton className="h-48 w-full" />
                </SkeletonRegion>
            </>
        );
    }
    if (id && (user.state === 'error' || !user.data)) {
        return (
            <>
                {header}
                <StateBlock
                    variant="error"
                    title={t('form.loadFailed')}
                    action={
                        <Button variant="strong" onClick={user.reload}>
                            {t('ui.retry', { ns: 'common' })}
                        </Button>
                    }
                />
            </>
        );
    }
    return (
        <>
            {header}
            <FormBody key={id ?? 'new'} id={id} initial={user.data ? fromUser(user.data) : EMPTY} />
        </>
    );
};

export default UserForm;
